import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../../localization/useTranslation.ts";
import {
    ContractExtractionRequest,
    ContractExtractionResponse,
    ContractSaveModel,
    CreationMode,
    DialogStep,
    EditableContractParty,
    SelectedObligationTemplate,
    UploadedFile,
    CreateContractPaneProps,
} from "./CreateContractPane.types.ts";
import { useValidator } from "@common/hooks/validation/index.ts";
import { post, sendWithFile } from "@common/api/apiShared.ts";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { useSelectableEntityTemplatesForCreation } from "@common/hooks/useSelectableItemQueries.ts";
import { Result } from "@common/api/result.ts";
import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { _arrayBufferToBase64 } from "@common/arrayBufferToBase64.ts";
import { ContractModel } from "../../contract/Contract.types.ts";
import { extractionToContractModel, getEditableStandardFields, keepEditableExtraction, toEditableContractParties } from "./extractionAdapter.ts";
import { useSelectableLegalEntitiesWithAddress } from "../../../legalEntity/useSelectableLegalEntityQuery.ts";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import {
    ContractStandardField,
    EntityTemplate,
    EntityTemplateField,
    getStandardFieldTranslationKey,
    TemplateEntityType,
} from "../../../entityTemplates/EntityTemplate.types.ts";
import { getFieldValue } from "../../contract/ContractValidator.hooks.ts";
import { hasFieldData } from "../../../entityTemplates/fieldValue.ts";
import { ValidationError } from "@common/validationError.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { groupFieldsByTemplateStep } from "../../../entityTemplates/templateStepFields/groupFieldsByTemplateStep.ts";
import { ContractStatus } from "../../Contracts.types.ts";
import { useIsPaneOpen, usePanes } from "../../../main/panes/usePanes";
import { usePane } from "../../../main/panes/paneLayout/usePane.ts";
import DocumentPane from "../../../main/panes/documentPane/DocumentPane";
import { publishStageDocumentOverlay } from "../../../main/panes/documentPane/stageDocumentOverlay";
import { buildCitationLookup } from "../../contractAiUpdate/citationLookup.ts";
import { PreviewableDocument } from "@common/components/documentPreview/DocumentPreview.types.ts";
import { isPreviewSupported } from "@common/components/documentPreview/DocumentPreview.hooks.ts";
import { Guid } from "@common/guid.ts";
import { maxAiDocumentSizeInBytes } from "@common/components/dotLegalFileUpload/maxDocumentSize";
import { buildContractDefaults, fieldsTheUserMayEdit } from "../../../entityTemplates/defaultValue/templateDefaults.ts";

interface ContractCreationValidationModel {
    entityTemplateId: string;
    startDate?: Date;
    endDate?: Date;
}

function toPreviewableDocument(file: UploadedFile): PreviewableDocument {
    return { documentId: file.previewDocumentId, name: file.fileName, fileName: file.fileName, localFile: file.file };
}

function toContractSaveFields(contract: Partial<ContractModel>, selectedTagIds: Array<string>): Partial<ContractSaveModel> {
    return {
        description: contract.description,
        referenceNumber: contract.referenceNumber,
        note: contract.note,
        contractTypeId: contract.contractTypeId,
        businessAreaIds: contract.businessAreas,
        signatureDate: contract.signatureDate,
        currencyCode: contract.currencyCode,
        totalAmount: contract.totalAmount,
        finalizedAmount: contract.finalizedAmount,
        paymentTermId: contract.paymentTermId,
        specialTerms: contract.specialTerms,
        contractExpiryNotificationInDays: contract.contractExpiryNotificationInDays,
        businessAreaAccessIds: contract.contractAccessBusinessAreas,
        additionalUserAccessIds: contract.additionalAccessUsers,
        visibleToAllUsers: contract.visibleToAllUsers,
        allGroupEntityAccess: contract.allGroupEntityAccess,
        customFieldValues: contract.customFieldValues,
        tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    };
}

export function useCreateContractPane(props: CreateContractPaneProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();
    const snackbar = useDotLegalSnackbar();
    const navigate = useNavigate();
    const { getContractUrl } = useUrlProvider();
    const { close, setHasUnsavedChanges, openOwned } = usePane();
    const { refetchPageBehindPanes, closeSlot } = usePanes();

    const sourceDocument = props.sourceDocument;
    // A stored document is only ever read by the AI, so there is no manual mode to start in or switch to.
    const [creationMode, setCreationMode] = useState<CreationMode>(sourceDocument ? "ai-assisted" : "manual");
    const [dialogStep, setDialogStep] = useState<DialogStep>("input");
    const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    // Empty until the user changes something, so the template's default values show through until then
    const [contractEdits, setContractEdits] = useState<Partial<ContractModel>>({});
    const [editedContractParties, setEditedContractParties] = useState<EditableContractParty[]>([]);
    const [selectedObligations, setSelectedObligations] = useState<SelectedObligationTemplate[]>([]);
    const [editedTagIds, setEditedTagIds] = useState<Array<string> | undefined>(undefined);
    const [showRequiredFieldErrors, setShowRequiredFieldErrors] = useState(false);
    const [showObligationErrors, setShowObligationErrors] = useState(false);
    const [hasObligationValidationErrors, setHasObligationValidationErrors] = useState(false);
    const [activeQuote, setActiveQuote] = useState<string | undefined>(undefined);
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    const selectableContractTemplatesQuery = useSelectableEntityTemplatesForCreation(TemplateEntityType.Contract, true);
    const selectableLegalEntitiesQuery = useSelectableLegalEntitiesWithAddress(dialogStep === "results");
    const contractTemplateQuery = useDotLegalQuery<EntityTemplate>(`/entity-templates/${selectedTemplateId}`, { enabled: !!selectedTemplateId });

    const selectedTemplate = selectableContractTemplatesQuery.data?.find((t) => t.id === selectedTemplateId);
    // The full template carries no such flag: the selectable view model is what reports that the user is
    // barred from a field this template insists on at creation time.
    const selectedTemplateMissingAccess = selectedTemplate?.missingRequiredAtCreationFieldAccess ?? false;

    const template = contractTemplateQuery.data;
    const templateFields = useMemo(() => template?.steps.flatMap((step) => step.fields) ?? [], [template]);

    const templateDefaults = buildContractDefaults(fieldsTheUserMayEdit(templateFields, platformFeatures.tags && permissions.canAccessTags));
    const withTemplateDefaults = (edits: Partial<ContractModel>): Partial<ContractModel> => ({
        ...templateDefaults.contract,
        ...edits,
    });
    const contract = withTemplateDefaults(contractEdits);
    const selectedTagIds = editedTagIds ?? templateDefaults.tagIds;

    const requiredAtCreationFields = templateFields.filter(
        (field) => field.requiredAtCreation && field.standardFieldIdentifier !== ContractStandardField.Status
    );

    const tagsRequiredAtCreation =
        platformFeatures.tags &&
        permissions.canAccessTags &&
        requiredAtCreationFields.some((field) => field.standardFieldIdentifier === ContractStandardField.Tags);

    const isRenderableField = (field: EntityTemplateField) => field.standardFieldIdentifier !== ContractStandardField.Tags || tagsRequiredAtCreation;

    const validation = useValidator<ContractCreationValidationModel>(
        // The contract name is no exception to the required-at-creation rule any more: the template always
        // marks it required at creation and the step it sits in renders it, so it is validated like the rest.
        (validators) => ({
            entityTemplateId: validators.validateNotEmpty((item) => item.entityTemplateId, translateString("contractTemplate")),
            endDate: validators.validateSameOrAfterStart(translateString("startDate"), translateString("endDate"), (item) => ({
                start: item.startDate,
                end: item.endDate,
            })),
        }),
        []
    );

    validation.check({
        entityTemplateId: selectedTemplateId,
        startDate: contract.startDate,
        endDate: contract.endDate,
    });

    const handleContractCreated = (contractId: string) => {
        // Creation from a legal entity's contract tab is an addition to the list the user is looking at, so
        // it stays put and refreshes behind the panel. Everywhere else the new contract is the destination.
        if (props.legalEntityId) {
            snackbar.show(translateString("contractCreated"));
            refetchPageBehindPanes();
            close();
            return;
        }

        close();
        navigate(getContractUrl(contractId, undefined, true));
    };

    const saveMutation = useDotLegalMutation((model: ContractSaveModel) => post<string>("/contracts/", model), {
        onSuccess: (response: Result<any>) => handleContractCreated(response.value().id),
    });

    const keepEditable = (extraction: ContractExtractionResponse) =>
        keepEditableExtraction(extraction, templateFields, permissions.canCreateLegalEntitiesInline);

    const extractMutation = useDotLegalMutation((entityTemplateId: string) => requestExtraction(entityTemplateId), {
        onSuccess: (response: Result<ContractExtractionResponse>) => {
            const extraction = keepEditable(response.value());
            setContractEdits(extractionToContractModel(extraction, selectedTemplateId, templateDefaults.contract));
            setEditedContractParties(toEditableContractParties(extraction, templateDefaults.counterPartyLegalEntityIds));
            setDialogStep("results");
        },
    });

    const rawExtractionResult = extractMutation.data?.value();
    const extractionResult = rawExtractionResult ? keepEditable(rawExtractionResult) : null;
    const editableStandardFields = getEditableStandardFields(templateFields);
    // Neither is a template field in this view, but each stands in for one, so it follows that field's permission.
    const canEditStatus = editableStandardFields.has(ContractStandardField.Status);
    const canEditContractParties = editableStandardFields.has(ContractStandardField.CounterParties);
    const citationForField = useMemo(() => buildCitationLookup(rawExtractionResult), [rawExtractionResult]);

    const extractedTemplateFields = extractionResult
        ? templateFields.filter(
              (field) =>
                  (field.standardFieldIdentifier && extractionResult.standardFields.some((f) => f.fieldIdentifier === field.standardFieldIdentifier)) ||
                  (field.customInputFieldId && extractionResult.customFields.some((f) => f.customInputFieldId === field.customInputFieldId))
          )
        : [];

    const manualFieldGroups = groupFieldsByTemplateStep(template, requiredAtCreationFields.filter(isRenderableField));
    const extractionFieldGroups = groupFieldsByTemplateStep(template, [...extractedTemplateFields, ...requiredAtCreationFields].filter(isRenderableField));

    async function requestExtraction(entityTemplateId: string) {
        if (sourceDocument) {
            return post<ContractExtractionResponse>(`/contracts/${sourceDocument.contractId}/documents/${sourceDocument.document.documentId}/extract`, {
                entityTemplateId,
            });
        }

        const request: ContractExtractionRequest = {
            documentBase64: await _arrayBufferToBase64(await uploadedFile!.file.arrayBuffer()),
            fileName: uploadedFile!.fileName,
            entityTemplateId,
        };
        return post<ContractExtractionResponse>("/contracts/extract", request);
    }

    const createFromExtractionMutation = useDotLegalMutation(
        async () => {
            const request = {
                contract: {
                    entityTemplateId: selectedTemplateId,
                    name: contract.name,
                    startDate: contract.startDate,
                    endDate: contract.endDate,
                    status: contract.status,
                    ...toContractSaveFields(contract, selectedTagIds),
                    customFieldValues: contract.customFieldValues ?? [],
                },
                contractParties: editedContractParties.map((party) => ({
                    legalEntityId: party.legalEntityId,
                    extractedName: party.legalEntityId ? undefined : party.name,
                    extractedAddress: party.legalEntityId ? undefined : party.address,
                    extractedCity: party.legalEntityId ? undefined : party.city,
                    extractedZipCode: party.legalEntityId ? undefined : party.zipCode,
                    extractedCountry: party.legalEntityId ? undefined : party.country,
                    extractedRegistrationNumber: party.legalEntityId ? undefined : party.registrationNumber,
                })),
            };

            const response = sourceDocument
                ? await post<{ id: string }>(`/contracts/${sourceDocument.contractId}/documents/${sourceDocument.document.documentId}/create-contract`, request)
                : await sendWithFile<{ id: string }>(
                      "POST",
                      "/contracts/create-from-extraction",
                      { ...request, documentFileName: uploadedFile?.fileName },
                      uploadedFile?.file,
                      trackUploadProgress()
                  );
            return response.value().id;
        },
        {
            onSuccess: (contractId: string) => {
                // The documents table opened a stored document, not this pane, so it does not leave with the pane.
                closeShownDocument();
                handleContractCreated(contractId);
            },
        }
    );

    const shownDocument = sourceDocument?.document ?? (uploadedFile ? toPreviewableDocument(uploadedFile) : undefined);
    const canPreviewShownDocument = shownDocument !== undefined && isPreviewSupported(shownDocument.fileName ?? "");
    // Refused here so the file is never sent to extraction, which would only fail on it after the upload.
    const isUploadedFileTooLargeForAi = uploadedFile !== null && uploadedFile.file.size > maxAiDocumentSizeInBytes;
    const canExtract = (sourceDocument !== undefined || uploadedFile !== null) && !!selectedTemplateId && !isUploadedFileTooLargeForAi;

    const isShownDocumentOpen = useIsPaneOpen(DocumentPane, (documentPaneProps) =>
        documentPaneProps.documents.some((document) => document.documentId === shownDocument?.documentId)
    );

    // Owned, so it closes with this pane however the pane goes: create, Cancel, the X, or the chat taking the rail.
    function openDocument(document: PreviewableDocument) {
        openOwned(DocumentPane, { documents: [document] });
    }

    // A citation is a reason to look at the document, so one clicked while it is closed brings it back.
    function showInDocument(quote: string) {
        setActiveQuote(quote);
        if (!isShownDocumentOpen && canPreviewShownDocument) {
            openDocument(shownDocument);
        }
    }

    // What the document on the stage should highlight, and, for an upload, the text to highlight it in,
    // republished only when either changes.
    const shownDocumentId = shownDocument?.documentId;
    const uploadedExtractedText = extractionResult?.extractedText;
    useEffect(() => {
        publishStageDocumentOverlay(
            shownDocumentId ? { documentId: shownDocumentId, citationChunkContent: activeQuote, extractedText: uploadedExtractedText } : undefined
        );
    }, [shownDocumentId, activeQuote, uploadedExtractedText]);

    useEffect(() => () => publishStageDocumentOverlay(undefined), []);

    function showUploadedDocument(file: UploadedFile) {
        if (isPreviewSupported(file.fileName)) {
            openDocument(toPreviewableDocument(file));
        } else {
            // A file swapped for one the previewer cannot show must not leave the previous file on show.
            closeShownDocument();
        }
    }

    function closeShownDocument() {
        if (isShownDocumentOpen) {
            closeSlot("stage");
        }
    }

    const resetContractState = () => {
        // The name outlives a template or mode change: it is the one thing already typed that the new
        // template cannot invalidate.
        setContractEdits((previous) => ({ name: previous.name }));
        setSelectedObligations([]);
        setEditedTagIds(undefined);
        setShowRequiredFieldErrors(false);
    };

    const onChange = {
        onTemplateChange: (entityTemplateId: string | null) => {
            if (entityTemplateId) {
                setHasUnsavedChanges(true);
                setSelectedTemplateId(entityTemplateId);
                resetContractState();
            }
        },
        onModeChange: (mode: CreationMode) => {
            setHasUnsavedChanges(true);
            setCreationMode(mode);
            setDialogStep("input");
            setUploadedFile(null);
            closeShownDocument();
            extractMutation.reset();
            resetContractState();
            setEditedContractParties([]);
        },
        onFileUploadChange: async (files: DotLegalFile[] | undefined) => {
            setHasUnsavedChanges(true);
            if (files && files[0]) {
                const file = { fileName: files[0].fileName, file: files[0].file, previewDocumentId: Guid.newGuid() };
                setUploadedFile(file);
                showUploadedDocument(file);
            } else {
                setUploadedFile(null);
                closeShownDocument();
            }
        },
    };

    useEffect(() => {
        if (selectableContractTemplatesQuery.data?.length === 1 && !selectedTemplateId) {
            setSelectedTemplateId(selectableContractTemplatesQuery.data[0].id);
        }
    }, [selectableContractTemplatesQuery.data, selectedTemplateId]);

    const onFieldChange = async (changeFunc: (contract: ContractModel) => void) => {
        setHasUnsavedChanges(true);
        setContractEdits((previous) => {
            const updated = withTemplateDefaults(previous) as ContractModel;
            changeFunc(updated);
            return updated;
        });
    };

    const onStatusChange = (status: ContractStatus) => {
        setHasUnsavedChanges(true);
        setContractEdits((previous) => ({ ...previous, status }));
    };

    const onContractDateChange = (field: "startDate" | "endDate", date: Date | undefined) => {
        setHasUnsavedChanges(true);
        setContractEdits((previous) => ({ ...previous, [field]: date }));
    };

    const onTagsChange = (tagIds: Array<string>) => {
        setHasUnsavedChanges(true);
        setEditedTagIds(tagIds);
    };

    const onObligationsChange = (obligations: Array<SelectedObligationTemplate>) => {
        setHasUnsavedChanges(true);
        setSelectedObligations(obligations);
    };

    const validateRequiredAtCreationFields = (fields: Array<EntityTemplateField>, values: Partial<ContractModel>, tagIds: Array<string>) => {
        const errors: Array<ValidationError> = [];
        const requiredTranslation = translateString("requiredField");

        for (const field of fields) {
            if (field.standardFieldIdentifier === ContractStandardField.Tags) {
                if (tagIds.length === 0) {
                    errors.push(new ValidationError(getStandardFieldTranslationKey(ContractStandardField.Tags), requiredTranslation));
                }
                continue;
            }

            const value = getFieldValue(field, values as ContractModel, tagIds);
            if (!hasFieldData(value)) {
                const fieldName = field.standardFieldIdentifier ? getStandardFieldTranslationKey(field.standardFieldIdentifier) : field.id;
                errors.push(new ValidationError(fieldName, requiredTranslation));
            }
        }

        if (values.startDate && values.endDate && new Date(values.startDate) >= new Date(values.endDate)) {
            errors.push(new ValidationError(getStandardFieldTranslationKey(ContractStandardField.EndDate), translateString("contractInvalidDateRange")));
        }

        return errors;
    };

    const requiredFieldValidationErrors = showRequiredFieldErrors ? validateRequiredAtCreationFields(requiredAtCreationFields, contract, selectedTagIds) : [];

    const validateManualCreation = (): boolean => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
        }
        const requiredFieldErrors = validateRequiredAtCreationFields(requiredAtCreationFields, contract, selectedTagIds);
        if (requiredFieldErrors.length > 0) {
            setShowRequiredFieldErrors(true);
        }
        return validation.anyHasErrors || requiredFieldErrors.length > 0;
    };

    const gotoObligationList = () => {
        if (validateManualCreation()) {
            return;
        }
        setDialogStep("obligationTemplates");
    };

    const createContract = async () => {
        if (validateManualCreation()) {
            return;
        }

        if (hasObligationValidationErrors) {
            setShowObligationErrors(true);
            return;
        }

        await saveMutation.mutateAsync({
            name: contract.name ?? "",
            entityTemplateId: selectedTemplateId,
            legalEntityId: props.legalEntityId,
            startDate: contract.startDate,
            endDate: contract.endDate,
            obligationTemplates: selectedObligations,
            ...toContractSaveFields(contract, selectedTagIds),
            // The server fills in the defaults of every other field
            answeredFieldIds: requiredAtCreationFields.map((field) => field.id),
        });
    };

    const extractContract = async () => {
        if (!canExtract) {
            return;
        }

        // An extraction takes minutes, and closing the panel while one runs throws the result away. The
        // discard prompt only fires on this flag, so a scan has to count as something to lose.
        setHasUnsavedChanges(true);
        await extractMutation.mutateAsync(selectedTemplateId);
    };

    const createContractFromExtraction = async () => {
        if (!contract.name) {
            snackbar.show(translateString("contractNameRequired"), "warning");
            return;
        }

        const requiredFieldErrors = validateRequiredAtCreationFields(requiredAtCreationFields, contract, selectedTagIds);
        if (requiredFieldErrors.length > 0) {
            setShowRequiredFieldErrors(true);
            return;
        }

        await createFromExtractionMutation.mutateAsync();
    };

    const removeContractParty = (partyId: string) => {
        setHasUnsavedChanges(true);
        setEditedContractParties((previous) => previous.filter((p) => p.id !== partyId));
    };

    const addContractPartyFromLegalEntity = (legalEntityId: string) => {
        setHasUnsavedChanges(true);
        const selectedLegalEntity = selectableLegalEntitiesQuery.data!.find((l) => l.id === legalEntityId)!;
        setEditedContractParties((previous) => [
            ...previous,
            {
                id: crypto.randomUUID(),
                legalEntityId,
                address: selectedLegalEntity.address,
                city: selectedLegalEntity.city,
                zipCode: selectedLegalEntity.zipCode,
                country: selectedLegalEntity.country,
                name: selectedLegalEntity.referenceNumber,
            },
        ]);
    };

    // Memoised for identity, not cost: ObligationTemplatesList re-runs its seeding effects on every new
    // array, and an unmemoised fallback would hand it one on every render.
    const selectedObligationTemplates = useMemo(() => selectedTemplate?.obligationTemplates ?? [], [selectedTemplate]);

    return {
        inProgress: saveMutation.isLoading || createFromExtractionMutation.isLoading,
        createContract,
        contract,
        validation,
        onChange,
        onFieldChange,
        onStatusChange,
        onContractDateChange,
        contractTemplatesIsLoading: selectableContractTemplatesQuery.isLoading,
        contractTemplates: selectableContractTemplatesQuery.data,
        selectedTemplateId,
        isLoadingTemplate: contractTemplateQuery.isLoading,
        creationMode,
        dialogStep,
        uploadedFile,
        extractionResult,
        extractContract,
        isExtracting: extractMutation.isLoading,
        canExtract,
        isUploadedFileTooLargeForAi,
        isFromStoredDocument: sourceDocument !== undefined,
        extractedDocumentName: sourceDocument?.document.name ?? uploadedFile?.fileName,
        canShowDocument: creationMode === "ai-assisted" && canPreviewShownDocument && !isShownDocumentOpen,
        showDocument: () => openDocument(shownDocument!),
        citationForField,
        showInDocument,
        createContractFromExtraction,
        canCreateFromExtraction: !!contract.name,
        isCreatingFromExtraction: createFromExtractionMutation.isLoading,
        isUploadingExtractedDocument: createFromExtractionMutation.isLoading && !sourceDocument && uploadedFile !== null,
        uploadPercentage,
        editedContractParties,
        canEditStatus,
        canEditContractParties,
        removeContractParty,
        addContractPartyFromLegalEntity,
        selectableLegalEntities: selectableLegalEntitiesQuery.data,
        selectableLegalEntitiesLoading: selectableLegalEntitiesQuery.isLoading,
        selectedObligationTemplates,
        setDialogStep,
        gotoObligationList,
        selectedObligations,
        onObligationsChange,
        showObligationErrors,
        onHasObligationValidationErrorsChange: setHasObligationValidationErrors,
        manualFieldGroups,
        extractionFieldGroups,
        selectedTagIds,
        onTagsChange,
        requiredFieldValidationErrors,
        selectedTemplateMissingAccess,
    };
}
